import React, { ReactNode, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { cn } from '../lib/utils';



// Main FilterPanel Component
interface FilterPanelProps {
  children: ReactNode;
  title?: string;
  className?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  activeFiltersCount?: number;
  onReset?: () => void;
  resetLabel?: string;
}

const FilterPanel = ({ 
  children, 
  title = "필터",
  className,
  collapsible = true,
  defaultCollapsed = false,
  activeFiltersCount = 0,
  onReset,
  resetLabel = "초기화"
}: FilterPanelProps) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  return (
    <Card className={cn('bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700', className)}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div 
            className={cn(
              "flex items-center space-x-2 flex-1",
              collapsible && "cursor-pointer"
            )}
            onClick={collapsible ? () => setIsCollapsed(!isCollapsed) : undefined}
          >
            <CardTitle className="text-lg flex items-center space-x-2 text-gray-900 dark:text-gray-100">
              <span>{title}</span>
              {activeFiltersCount > 0 && (
                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                  {activeFiltersCount}개 적용
                </span>
              )}
              {collapsible && (
                <span className="text-gray-500 dark:text-gray-400">
                  {isCollapsed ? '▼' : '▲'}
                </span>
              )}
            </CardTitle>
          </div>
          {onReset && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onReset();
              }}
              className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md transition-colors ml-2"
            >
              {resetLabel}
            </button>
          )}
        </div>
      </CardHeader>
      {(!collapsible || !isCollapsed) && (
        <CardContent className="space-y-4">
          {children}
        </CardContent>
      )}
    </Card>
  );
};

// Search Component
interface SearchFilterProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  debounceMs?: number;
}

const SearchFilter = ({ 
  value, 
  onChange, 
  placeholder = "검색...", 
  label,
  className,
  debounceMs = 300
}: SearchFilterProps) => {
  const [localValue, setLocalValue] = React.useState(value);
  const timeoutRef = React.useRef<NodeJS.Timeout>();

  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (newValue: string) => {
    setLocalValue(newValue);
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      onChange(newValue);
    }, debounceMs);
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
      )}
      <input
        type="text"
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
      />
    </div>
  );
};

// Date Range Component
interface DateRangeFilterProps {
  startValue: string;
  endValue: string;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  startLabel?: string;
  endLabel?: string;
  className?: string;
}

const DateRangeFilter = ({
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  startLabel = "시작일",
  endLabel = "종료일",
  className
}: DateRangeFilterProps) => {
  const [localStartValue, setLocalStartValue] = React.useState(startValue || '');
  const [localEndValue, setLocalEndValue] = React.useState(endValue || '');

  // 외부 값이 변경되면 로컬 상태 동기화
  React.useEffect(() => {
    setLocalStartValue(startValue || '');
  }, [startValue]);

  React.useEffect(() => {
    setLocalEndValue(endValue || '');
  }, [endValue]);

  const handleStartChange = (value: string) => {
    setLocalStartValue(value);
    onStartChange(value);
  };

  const handleEndChange = (value: string) => {
    setLocalEndValue(value);
    onEndChange(value);
  };

  const handleStartBlur = () => {
    // 블러 시에도 최종 값 전달
    onStartChange(localStartValue);
  };

  const handleEndBlur = () => {
    // 블러 시에도 최종 값 전달
    onEndChange(localEndValue);
  };

  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-2 gap-4', className)}>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {startLabel}
        </label>
        <input
          type="date"
          value={localStartValue}
          onChange={(e) => handleStartChange(e.target.value)}
          onBlur={handleStartBlur}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {endLabel}
        </label>
        <input
          type="date"
          value={localEndValue}
          onChange={(e) => handleEndChange(e.target.value)}
          onBlur={handleEndBlur}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>
    </div>
  );
};

// Checkbox Group Component
interface CheckboxOption {
  value: string;
  label: string;
  color?: string;
  variant?: 'default' | 'badge';
}

interface CheckboxGroupFilterProps {
  options: CheckboxOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  label?: string;
  className?: string;
  layout?: 'horizontal' | 'vertical';
}

const CheckboxGroupFilter = ({
  options,
  selectedValues,
  onChange,
  label,
  className,
  layout = 'horizontal'
}: CheckboxGroupFilterProps) => {
  const handleToggle = (value: string) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label}
        </label>
      )}
      <div className={cn(
        'flex gap-2',
        layout === 'horizontal' ? 'flex-wrap' : 'flex-col'
      )}>
        {options.map((option) => (
          <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedValues.includes(option.value)}
              onChange={() => handleToggle(option.value)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
            />
            {option.variant === 'badge' ? (
              <span className={cn(
                'px-2 py-1 rounded-full text-xs font-medium',
                option.color || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              )}>
                {option.label}
              </span>
            ) : (
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {option.label}
              </span>
            )}
          </label>
        ))}
      </div>
    </div>
  );
};

// Grid Layout Component
interface FilterGridProps {
  children: ReactNode;
  columns?: 1 | 2 | 3 | 4;
  className?: string;
}

const FilterGrid = ({ children, columns = 3, className }: FilterGridProps) => {
  const gridClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
  };

  return (
    <div className={cn('grid gap-4', gridClasses[columns], className)}>
      {children}
    </div>
  );
};

// Compound exports
FilterPanel.Search = SearchFilter;
FilterPanel.DateRange = DateRangeFilter;
FilterPanel.CheckboxGroup = CheckboxGroupFilter;
FilterPanel.Grid = FilterGrid;

export { FilterPanel };
export type {
  FilterPanelProps,
  SearchFilterProps,
  DateRangeFilterProps,
  CheckboxGroupFilterProps,
  CheckboxOption,
  FilterGridProps
};
